import { useState, useEffect, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Table } from "antd";
import { MedicationsDispensedContext } from "./MedicationsDispensedContext";
import { ReactComponent as Square } from "../../../../../assets/square.svg";
import SelectTrayPillsForCanister from "./SelectTrayPillsForCanister";
import {
  getNumberOfDaysInDateRange,
  countWeekdaysInRange,
} from "../../../../../functions/dates";
import styles from "./DispensedMedicationsTable.module.css";
import "./DispensedMedicationsTableAntStyles.css";

const DispensedMedicationsTableForTrays = ({
  user,
  startDateLastWeek,
  stopDateLastWeek,
  selectedDateRange,
  applicableDates,
  selectedDayTray,
  trayProductSelectionLookup,
  onTrayProductSelectionLookup,
  trayProductsSelectedDay,
  onTrayProductsSelectedDay,
  trayProducts,
  onTrayProducts,
  showCanisterPlacementPopup,
  onShowCanisterPlacementPopup,
  dailyAverageTrays,
  onDailyAverageTrays,
}) => {
  const {
    handleReadyForServerRequest,
    trayProductsAndCountsData,
    handleDispensingOrderIds,
    batchesSelectedDateRange,
    handleTrayBargraphData,
  } = useContext(MedicationsDispensedContext);

  const LIMIT_NUM_PRODUCTS_HIGHEST_QTY = 10000;
  const { t } = useTranslation("trupakDashboard");
  const [dataTrayTable, setDataTrayTable] = useState([]);
  const [allTrayDrugsSelected, setAllTrayDrugsSelected] = useState(false);
  const [tableTitle, setTableTitle] = useState("");
  const [trayProductsFiltered, setTrayProductsFiltered] = useState([]);
  const [trayProductsSelectedDayFiltered, setTrayProductsSelectedDayFiltered] =
    useState([]);
  const [selectedTrayCount, setSelectedTrayCount] = useState(0);
  const [newTrayFillDailyAverage, setNewTrayFillDailyAverage] = useState(0);

  // SELECTED DATE RANGE
  // Called upon page load and when date range is changed
  // Gets data for Most Filled Table with selected data range
  useEffect(() => {
    /**
     * If batchesSelectedDateRange array has length, sets:
     *  - readyForServerRequest
     *  - totalWholePillDispensedTraySelectedDateRange
     *  - trayBargraphData
     *  - trayProductSelectionLookup,
     *  - dispensingOrderIds
     *  - packagedIdsList
     *
     * Else, resets the following to default (starting) values:
     *  - trayProducts,
     *  - trayProductsSelectedDay,
     *  - trayProductSelectionLookup,
     *  - trayBargraphData,
     *  - selectedTrayDate
     *  - readyForServerRequest
     **/
    if (batchesSelectedDateRange.length) {
      let trayPackageIds = [];
      let trayPackageIdsDates = [];
      let tempDispensingOrderIds = [];
      let tempDispensingOrderIdsDates = [];
      let trayProducts_temp = [];
      let dayOfWeek;
      let tempTrayProductSelectionLookup = {};

      // let trayCount = 0;
      for (const batchSelectedDateRange of batchesSelectedDateRange) {
        dayOfWeek = new Date(batchSelectedDateRange.createdAt).getDay(); // gets day of corresponding local time

        if (
          !tempDispensingOrderIdsDates.includes(
            `${batchSelectedDateRange.dispensingOrderId};${dayOfWeek}`,
          )
        ) {
          tempDispensingOrderIds.push(batchSelectedDateRange.dispensingOrderId);
          tempDispensingOrderIdsDates.push(
            `${batchSelectedDateRange.dispensingOrderId};${dayOfWeek}`,
          );
        }
        for (const dispensingDetail of batchSelectedDateRange.dispensingDetails) {
          // if (dispensingDetail.isTrayMed && !dispensingDetail.isPartial) {
          if (dispensingDetail.isTrayMed) {
            if (
              !trayPackageIdsDates.includes(
                `${dispensingDetail.packageId};${dayOfWeek}`,
              )
            ) {
              trayPackageIds.push(dispensingDetail.packageId);
              trayPackageIdsDates.push(
                `${dispensingDetail.packageId};${dayOfWeek}`,
              );
              trayProducts_temp.push({
                dispensingOrderId: batchSelectedDateRange.dispensingOrderId,
                packageId: dispensingDetail.packageId,
                productName: dispensingDetail.productDescription,
                qty: dispensingDetail.isPartial ? 0 : dispensingDetail.quantity,
                partialQty: dispensingDetail.isPartial
                  ? dispensingDetail.quantity
                  : 0,
                trays: 0,
                dayOfWeek: dayOfWeek,
                manufacturer: dispensingDetail.productManufacturer,
              });
            } else {
              if (
                trayProducts_temp.find(
                  (p) =>
                    p.packageId === dispensingDetail.packageId &&
                    p.dayOfWeek === dayOfWeek,
                )
              ) {
                const product = trayProducts_temp.find(
                  (p) =>
                    p.packageId === dispensingDetail.packageId &&
                    p.dayOfWeek === dayOfWeek,
                );
                if (product.dayOfWeek === dayOfWeek) {
                  if (!dispensingDetail.isPartial) {
                    product.qty += dispensingDetail.quantity;
                  } else if (
                    dispensingDetail.isPartial &&
                    product.dayOfWeek === dayOfWeek
                  ) {
                    product.partialQty += dispensingDetail.quantity;
                  }
                }
              }
            }
          }
        } // end for batch of
      }

      trayProducts_temp.sort(compareByQuantityDescending);

      trayProducts_temp.forEach((product) => {
        tempTrayProductSelectionLookup[product.packageId] = {
          selected: false,
          product: product.productName,
          manufacturer: product.manufacturer,
        };
      });
      handleReadyForServerRequest(true);

      onTrayProductSelectionLookup(tempTrayProductSelectionLookup);
      onTrayProducts(trayProducts_temp);
      handleDispensingOrderIds(tempDispensingOrderIds);

      // convert packageIds array to string list
      let tempPackageIdsList = "";

      trayPackageIds.forEach((packageId) => {
        tempPackageIdsList += `${packageId},`;
      });

      tempPackageIdsList = tempPackageIdsList.substring(
        0,
        tempPackageIdsList.length - 1,
      );
    } else {
      onTrayProducts([]);
      onTrayProductsSelectedDay([]);
      onTrayProductSelectionLookup({});
      handleTrayBargraphData([]);
      handleReadyForServerRequest(false);
    }
  }, [
    user,
    batchesSelectedDateRange,
    onTrayProducts,
    onTrayProductSelectionLookup,
    onTrayProductsSelectedDay,
    t,
  ]);

  useEffect(() => {
    // gets tray counts for each product for Most Filled table
    if (
      trayProductsAndCountsData.hasOwnProperty("packageIdTrayCounts") &&
      trayProductsAndCountsData.packageIdTrayCounts.length
    ) {
      let tempProductTrayCountLookup = {};

      for (const packageIdTrayCount of trayProductsAndCountsData.packageIdTrayCounts) {
        if (!(packageIdTrayCount.packageId in tempProductTrayCountLookup)) {
          tempProductTrayCountLookup[packageIdTrayCount.packageId] =
            packageIdTrayCount?.totalTrayCount || 0;
        } else {
          tempProductTrayCountLookup[packageIdTrayCount.packageId] +=
            packageIdTrayCount?.totalTrayCount || 0;
        }
      }

      let trayProductsTemp = [...trayProducts];
      for (const tempTrayProduct of trayProductsTemp) {
        if (
          tempProductTrayCountLookup[tempTrayProduct.packageId] &&
          !selectedDayTray &&
          selectedDayTray !== 0
        ) {
          tempTrayProduct.trays =
            tempProductTrayCountLookup[tempTrayProduct.packageId];
        } else if (!selectedDayTray && selectedDayTray !== 0) {
          tempTrayProduct.trays = 0;
        }
      }
      // Set the number of trays
      onTrayProducts(trayProductsTemp);
      // setUpdateProductCount(false);
    }
  }, [trayProductsAndCountsData]);

  useEffect(() => {
    let tempSelectedTrayCount = 0;
    for (const [key, value] of Object.entries(trayProductSelectionLookup)) {
      const packageId = key;
      const selected = value.selected;
      if (
        trayProductsAndCountsData.hasOwnProperty("packageIdTrayCounts") &&
        trayProductsAndCountsData.packageIdTrayCounts.length
      ) {
        const foundTrayProductsAndCountDataObj =
          trayProductsAndCountsData.packageIdTrayCounts.find(
            (obj) => obj.packageId === packageId && selected,
          );
        if (foundTrayProductsAndCountDataObj) {
          tempSelectedTrayCount +=
            foundTrayProductsAndCountDataObj?.totalTrayCount || 0;
        }
      }
      setSelectedTrayCount(tempSelectedTrayCount);
    }
  }, [trayProductSelectionLookup, trayProductsAndCountsData]);

  useEffect(() => {
    /*
      Note that this useEffect is triggered when this page loads and when 
      trayProducts is updated.  When this page is first loaded, the tray count
      for each product is 0.  However, trayProducts is updated with tray counts
      subsequently within MetricsChartViewTrays, causing this code to rerun and 
      reflect the updates to the tray counts.  
    */
    const trayProductsCombinedDrugIds = [];
    for (const product of trayProducts) {
      const index = trayProductsCombinedDrugIds.findIndex(
        (trayProductCombinedDrugId) =>
          trayProductCombinedDrugId.packageId === product.packageId,
      );

      if (index === -1) {
        trayProductsCombinedDrugIds.push({ ...product });
      } else {
        trayProductsCombinedDrugIds[index].qty += product.qty;
        trayProductsCombinedDrugIds[index].partialQty += product.partialQty;
      }
    }
    setTrayProductsFiltered(
      trayProductsCombinedDrugIds.filter((_, index) => {
        if (index < LIMIT_NUM_PRODUCTS_HIGHEST_QTY) {
          return true;
        } else {
          return false;
        }
      }),
    );
  }, [trayProducts]);

  useEffect(() => {
    let totalTraysForSelectedProducts = 0;
    for (const [key, value] of Object.entries(trayProductSelectionLookup)) {
      if (value.selected) {
        totalTraysForSelectedProducts += value.trayCount;
      }
    }
    let newDailyAverage = 0;

    if (
      parseFloat(dailyAverageTrays) > 0 &&
      selectedDateRange?.startDate &&
      selectedDateRange?.endDate
    ) {
      let numOfDays;

      if (selectedDayTray || selectedDayTray === 0) {
        numOfDays = countWeekdaysInRange(
          selectedDateRange.startDate,
          selectedDateRange.endDate,
          selectedDayTray,
        );
      } else {
        numOfDays = getNumberOfDaysInDateRange(
          selectedDateRange.startDate,
          selectedDateRange.endDate,
        );
      }

      if (
        numOfDays &&
        trayProductsAndCountsData &&
        trayProductsAndCountsData?.totalTrayCount &&
        trayProductsAndCountsData.totalTrayCount.length
      ) {
        newDailyAverage =
          (parseFloat(
            trayProductsAndCountsData.totalTrayCount[0].totalTrayCount,
          ) -
            parseFloat(selectedTrayCount)) /
          numOfDays;
      }
    }
    if (newDailyAverage < 0.02) {
      newDailyAverage = 0.0;
    }
    setNewTrayFillDailyAverage(parseFloat(newDailyAverage).toFixed(2));
  }, [
    trayProductSelectionLookup,
    selectedDateRange,
    selectedDayTray,
    dailyAverageTrays,
    onDailyAverageTrays,
    selectedTrayCount,
  ]);

  useEffect(() => {
    setTrayProductsSelectedDayFiltered(
      trayProductsSelectedDay.filter((_, index) => {
        if (index < LIMIT_NUM_PRODUCTS_HIGHEST_QTY) {
          return true;
        } else {
          return false;
        }
      }),
    );
  }, [trayProductsSelectedDay]);

  const handleRowSelection = useCallback(
    (packageId) => {
      let tempRowStatus = { ...trayProductSelectionLookup };
      if (tempRowStatus[packageId].selected === true) {
        tempRowStatus[packageId].selected = false;
        setAllTrayDrugsSelected(false);
      } else {
        if (tempRowStatus[packageId].selected === false) {
          tempRowStatus[packageId].selected = true;
        }
      }
      onTrayProductSelectionLookup(tempRowStatus);
    },
    [onTrayProductSelectionLookup, trayProductSelectionLookup],
  );

  useEffect(() => {
    if (selectedDayTray !== null) {
    }
  }, []);

  useEffect(() => {
    const hasKey = (obj) => {
      for (const key in obj) {
        if (Object.hasOwn(obj, key)) {
          return true;
        }
      }
      return false;
    };
    if (hasKey(trayProductSelectionLookup)) {
      const createIDSquare = (id) => {
        const IDSquare = (
          <div
            className={
              trayProductSelectionLookup[id]?.selected === true
                ? `${styles.DispensedMedicationsTable__idSquareContainer} ${styles["DispensedMedicationsTable__idSquareContainer--active"]}`
                : styles.DispensedMedicationsTable__idSquareContainer
            }
            onClick={() => handleRowSelection(id)}
          >
            <Square width="24px" height="24px" />
          </div>
        );
        return IDSquare;
      };

      let trayProds;
      if (selectedDayTray !== null) {
        trayProds = trayProductsSelectedDayFiltered;
      } else {
        trayProds = trayProductsFiltered;
      }

      setDataTrayTable(
        trayProds
          .map((product) => {
            return {
              key: `${product.packageId}_tray;${product.dayOfWeek}`,
              idSquare: createIDSquare(product.packageId),
              packageId_tray: product.packageId,
              medication_tray: product.productName,
              quantity_tray: product.qty,
              partial_quantity_tray: product.partialQty,
              trays: product.trays,
            };
          })
          .filter((product) => product.trays > 0),
      );
    } else {
      setDataTrayTable([]);
    }
  }, [
    trayProductsFiltered,
    trayProductsSelectedDayFiltered,
    trayProductSelectionLookup,
    selectedDayTray,
    handleRowSelection,
  ]);

  useEffect(() => {
    let tempTableTitle = "";
    if (selectedDayTray || selectedDayTray === 0) {
      const selectedDate = new Date(selectedDateRange.startDate);
      selectedDate.setDate(selectedDate.getDate() + selectedDayTray);
    }

    tempTableTitle = (
      <div className={styles.DispensedMedicationsTable__tableTitle}>
        <h2 className={styles.DispensedMedicationsTable__tableTitleMain}>
          {t("Most Filled")}
        </h2>
        {/* <p className={styles.DispensedMedicationsTable__limitMessage}>
          ({`Top ${LIMIT_NUM_PRODUCTS_HIGHEST_QTY} By Qty Filled`})
        </p> */}
      </div>
    );

    setTableTitle(tempTableTitle);
  }, [selectedDayTray, selectedDateRange, user, t]);

  const getUniqueValues = (arr, field) => {
    const seen = new Set();
    return arr
      .map((item) => item[field])
      .filter((val) => {
        if (seen.has(val)) return false;
        seen.add(val);
        return true;
      });
  };

  const trayColumnsInfo = [
    {
      title: (
        <div
          className={
            allTrayDrugsSelected
              ? `${styles.DispensedMedicationsTable__idSquareContainer} ${styles["DispensedMedicationsTable__idSquareContainer--active"]}`
              : styles.DispensedMedicationsTable__idSquareContainer
          }
          onClick={() => handleAllTrayDrugsSelected()}
        >
          <Square width="24px" height="24px" />
        </div>
      ),
      dataIndex: "idSquare",
      key: "idSquare",
      width: "26px",
    },
    {
      title: t("Drug ID"),
      dataIndex: "packageId_tray",
      key: "packageId_tray",
      sorter: (a, b) => a.packageId_tray.localeCompare(b.packageId_tray),
      sortDirections: ["ascend", "descend"],
      filters: getUniqueValues(dataTrayTable, "packageId_tray").map(
        (value, index) => ({
          text: value,
          value,
          key: `packageId_tray-${index}`,
        }),
      ),
      onFilter: (value, record) => record.packageId_tray.indexOf(value) === 0,
      showSorterTooltip: { target: "full-header" },
      width: "180px",
    },
    {
      title: t("Description"),
      dataIndex: "medication_tray",
      key: "medication_tray",
      sorter: (a, b) => a.medication_tray.localeCompare(b.medication_tray),
      sortDirections: ["ascend", "descend"],
      filters: getUniqueValues(dataTrayTable, "medication_tray").map(
        (value, index) => ({
          text: value,
          value,
          key: `medication_tray-${index}`,
        }),
      ),
      onFilter: (value, record) => record.medication_tray.indexOf(value) === 0,
    },

    {
      align: "right",
      title: t("Partial QTY"),
      dataIndex: "partial_quantity_tray",
      key: "partial_quantity_tray",
      sorter: (a, b) => a.partial_quantity_tray - b.partial_quantity_tray,
      sortDirections: ["ascend", "descend"],
      filters: getUniqueValues(dataTrayTable, "partial_quantity_tray").map(
        (value, index) => ({
          text: value,
          value,
          key: `partial_quantity_tray-${index}`,
        }),
      ),
      onFilter: (value, record) => record.partial_quantity_tray === value,
      onCell: (record, rowIndex) => ({
        onClick: (ev) => {
          console.log("row & cell info: ", record, rowIndex, ev);
        },
        style: { cursor: "pointer" },
      }),
      width: "175px",
    },

    {
      align: "right",
      title: t("Whole QTY"),
      dataIndex: "quantity_tray",
      key: "quantity_tray",
      sorter: (a, b) => a.quantity_tray - b.quantity_tray,
      sortDirections: ["ascend", "descend"],
      filters: getUniqueValues(dataTrayTable, "quantity_tray").map(
        (value, index) => ({
          text: value,
          value,
          key: `quantity_tray-${index}`,
        }),
      ),
      onFilter: (value, record) => record.quantity_tray === value,
      onCell: (record, rowIndex) => ({
        onClick: (ev) => {
          console.log("row & cell info: ", record, rowIndex, ev);
        },
        style: { cursor: "pointer" },
      }),
      width: "175px",
    },
    {
      align: "right",
      title: t("Total Trays"),
      dataIndex: "trays",
      key: "trays",
      sorter: (a, b) => a.trays - b.trays,
      sortDirections: ["ascend", "descend"],
      filters: getUniqueValues(dataTrayTable, "trays").map((value, index) => ({
        text: value,
        value,
        key: `trays-${index}`,
      })),
      onFilter: (value, record) => record.trays === value,
      width: "175px",
    },
  ];

  const compareByQuantityDescending = (a, b) => {
    if (a.qty < b.qty) {
      return 1;
    } else if (a.qty > b.qty) {
      return -1;
    } else if (a.productName.toUpperCase() < b.productName.toUpperCase()) {
      return -1;
    }
    return 1;
  };

  const data = dataTrayTable;
  const columnsInfo = trayColumnsInfo;

  const columns = columnsInfo.map((column) => {
    return {
      title: column.title,
      dataIndex: column.dataIndex,
      key: column.key,
      sortColumnKey: column.key,
      width: column.width ? column.width : null,
      sorter: column.sorter ? column.sorter : null,
      filters: column.filters ? column.filters : null,
      onFilter: column.onFilter ? column.onFilter : null,
      ellipsis: true,
      onCell: column.onCell ? column.onCell : null,
    };
  });

  const handleAllTrayDrugsSelected = () => {
    let tempRowStatus = { ...trayProductSelectionLookup };

    if (allTrayDrugsSelected) {
      Object.keys(trayProductSelectionLookup).forEach((key) => {
        tempRowStatus[key].selected = false;
      });
      setAllTrayDrugsSelected(false);
    } else {
      if (selectedDayTray === null) {
        Object.keys(trayProductSelectionLookup).forEach((key) => {
          tempRowStatus[key].selected = true;
        });
        setAllTrayDrugsSelected(true);
      } else {
        const validPackageIds = trayProductsSelectedDay.map(
          (product) => product.packageId,
        );
        Object.keys(trayProductSelectionLookup).forEach((key) => {
          if (validPackageIds.includes(key)) {
            tempRowStatus[key].selected = true;
          } else {
            tempRowStatus[key].selected = false;
          }
        });
      }
      setAllTrayDrugsSelected(true);
    }
    onTrayProductSelectionLookup(tempRowStatus);
  };

  const handleCalculate = () => {
    onShowCanisterPlacementPopup(true);
  };

  if (showCanisterPlacementPopup) {
    return (
      <div
        className={
          styles.DispensedMedicationsTable__SelectTrayPillsForCanisterContainer
        }
      >
        <SelectTrayPillsForCanister
          user={user}
          trayProductSelectionLookup={trayProductSelectionLookup}
          onShowCanisterPlacementPopup={onShowCanisterPlacementPopup}
          trayProducts={trayProductsFiltered}
          trayProductsSelectedDay={trayProductsSelectedDayFiltered}
          selectedDayTray={selectedDayTray}
          startDateLastWeek={startDateLastWeek}
          stopDateLastWeek={stopDateLastWeek}
          selectedDateRange={selectedDateRange}
          applicableDates={applicableDates}
        />
      </div>
    );
  }

  return (
    <div
      className={`${styles.DispensedMedicationsTable__tableContainer} DispensedMedicationsTable__AntStyle`}
    >
      {tableTitle}
      <p className={styles.DispensedMedicationsTable__canisterPlacementMessage}>
        Your new tray fill daily average will be&nbsp;
        <span>{newTrayFillDailyAverage}</span>&nbsp;if you canister the selected
        Drug IDs
      </p>
      <div className="DispensedMedicationsTable__tableTypeTrays">
        <Table
          columns={columns}
          dataSource={data}
          // onChange={handleChange}
          scroll={{ y: `calc(70vh - 460px)` }}
          pagination={{ pageSize: 10 }}
          showSorterTooltip={{ target: "sorter-icon" }}
        />
      </div>
    </div>
  );
};

export default DispensedMedicationsTableForTrays;
